import axios, { AxiosError } from 'axios'
import config from 'config'
import convertObjectToQueryString from 'utils/convertObjectToQueryString'

import { OctaneLogger } from '../../integrations/datadog/OctaneLogger'

export interface SearchOutdoorPowerEquipmentOptions {
    query: string
    condition?: 'new' | 'used'
}

export interface OutdoorPowerEquipment {
    id: number
    uuid: string
    name: string
    year: number
    make: string
    model: string
    sku: string | null
    equipmentType: string
    equipmentTypeLabel: string
    equipmentSubType: string
    equipmentSubTypeLabel: string
    msrp: number
    condition: string
}

interface ResponseOutdoorPowerEquipment {
    id: number
    uuid: string
    name: string
    year: number
    make: string
    model: string
    sku: string | null
    equipment_type: string
    equipment_type_label: string
    equipment_sub_type: string
    equipment_sub_type_label: string
    msrp: number
    condition: string
}

function toOutdoorPowerEquipment(
    result: ResponseOutdoorPowerEquipment,
): OutdoorPowerEquipment {
    return {
        id: result.id,
        uuid: result.uuid,
        name: result.name,
        year: result.year,
        make: result.make,
        model: result.model,
        sku: result.sku,
        equipmentType: result.equipment_type,
        equipmentTypeLabel: result.equipment_type_label,
        equipmentSubType: result.equipment_sub_type,
        equipmentSubTypeLabel: result.equipment_sub_type_label,
        msrp: result.msrp,
        condition: result.condition,
    }
}

export default async function searchOutdoorPowerEquipment(
    options: SearchOutdoorPowerEquipmentOptions,
    abortController: AbortController,
) {
    const url = `${config.API_BASE_URL}/api/v3/outdoor-power-equipment/`

    const queryString = convertObjectToQueryString(
        {
            q: options.query,
            condition: options.condition,
        },
        true,
    )

    try {
        const { data } = await axios.get(`${url}?${queryString}`, {
            signal: abortController.signal,
            withCredentials: true,
        })

        if (!Array.isArray(data?.results)) {
            throw new Error('Invalid outdoor power equipment response shape')
        }

        return data.results.map(
            toOutdoorPowerEquipment,
        ) as OutdoorPowerEquipment[]
    } catch (error) {
        if (!axios.isAxiosError(error)) {
            throw error
        }

        const axiosError = error as AxiosError

        if (axiosError.code !== AxiosError.ERR_CANCELED) {
            OctaneLogger.axiosError(
                `Failed to search outdoor power equipment: ${axiosError.message}`,
                axiosError,
            )

            throw new Error(axiosError.message)
        }

        throw axiosError
    }
}
